import { useEffect, useMemo, useRef, useState } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { Asset, AssetReview, Comment, ReviewLink } from '@vidmakr/bindings/types';
import type { Timestamp } from 'spacetimedb';
import { CheckCircle2, Circle, MessageSquare } from 'lucide-react';
import { buildAssetOverview, byTimecode, formatTimecode, laneMarks, uuidv7, type AssetRound, type OverviewSource, type OverviewThread } from '@vidmakr/shared';
import { useMediaUrls } from '@/lib/media';
import { reducers } from '@/lib/stdb';
import { attempt } from '@/lib/toast';
import { timeAgo } from '@/lib/format';
import { useMediaQuery } from '@/lib/hooks';
import { CommentBody } from './mentions';
import { Button, Input, cx } from './ui';
import { Lane, SourceSwatch, StatusIcon, TimeRuler } from './ReviewViz';
import { BufferedVideo } from './BufferedVideo';

/**
 * Every link's feedback on an asset shared for review: the file with each
 * link's comments as a row of dots under it, the review rounds (each round's
 * result and every link's decision; click one to look at that round), and
 * the conversations, all together or one link at a time.
 *
 * Rounds review the same file (an asset link always plays that file), so
 * unlike a project's versions there is nothing to compare side by side.
 */

const ms = (ts: Timestamp | undefined) => (ts ? Number(ts.microsSinceUnixEpoch / 1000n) : undefined);
const day = (t?: number) => (t ? new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');

const RESULT: Record<AssetRound['result'], { label: string; tone: string }> = {
  approved: { label: 'Approved', tone: 'text-ok' },
  changes_requested: { label: 'Changes requested', tone: 'text-warn' },
  in_review: { label: 'In review', tone: 'text-accent-2' },
  no_decision: { label: 'No decision', tone: 'text-faint' },
  ended: { label: 'Review ended', tone: 'text-faint' },
};

/** `page`: laid out for the full-page view (bigger player, taller conversation list; player and conversations stack below 1280px). */
export function AssetReviewOverview({ asset, links, page = false }: { asset: Asset; links: readonly ReviewLink[]; page?: boolean }) {
  const ids = useMemo(() => links.map((l) => l.id).sort(), [links]);
  const [rows] = useTable(tables.comment.where((c) => (ids.length ? ids.map((id) => c.linkId.eq(id)).reduce((a, b) => a.or(b)) : c.linkId.eq('-'))));
  const [reviewRows] = useTable(tables.assetReview.where((r) => r.assetId.eq(asset.id)));
  const [decisionRows] = useTable(tables.reviewDecision.where((d) => d.assetId.eq(asset.id)));
  const review = reviewRows[0] as AssetReview | undefined;
  const comments = rows as readonly Comment[];
  const o = useMemo(
    () =>
      buildAssetOverview({
        nowMs: Date.now(),
        durationUs: asset.durationUs,
        review: review ? { round: review.round, status: review.status } : null,
        links: links.map((l) => ({ id: l.id, label: l.label, revoked: l.revoked, expiresAtMs: ms(l.expiresAt), allowDecisions: l.allowDecisions, createdAtMs: ms(l.createdAt)! })),
        decisions: decisionRows.map((d) => ({
          id: d.id,
          round: d.round,
          userId: d.userId,
          name: d.name,
          decision: d.decision,
          note: d.note,
          createdAtMs: ms(d.createdAt)!,
          linkId: d.linkId,
        })),
        comments: comments
          .filter((c) => ids.includes(c.linkId))
          .map((c) => ({
            id: c.id,
            parentId: c.parentId,
            linkId: c.linkId,
            authorUserId: c.authorUserId,
            authorName: c.authorName,
            body: c.body,
            timeUs: c.timeUs,
            endUs: c.endUs,
            resolved: c.resolved,
            createdAtMs: ms(c.createdAt)!,
            drawing: c.drawing,
            assetRound: c.assetRound,
          })),
      }),
    [asset.durationUs, review, links, decisionRows, comments, ids],
  );
  const [round, setRound] = useState<number | null>(null);
  const [tab, setTab] = useState<string>('all');
  const [focus, setFocus] = useState<string | null>(null);
  // Start on the current round once it is known (or every round when there is none).
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !o.rounds.length) return;
    started.current = true;
    setRound(o.currentRound || null);
  }, [o.rounds.length, o.currentRound]);
  const fps = asset.fps || 30;
  const sourceById = useMemo(() => new Map(o.sources.map((s) => [s.id, s])), [o]);
  const inRound = useMemo(() => o.threads.filter((t) => round === null || t.round === round), [o, round]);
  const linkSources = o.sources.filter((s) => s.kind === 'link');
  const feed = inRound.filter((t) => tab === 'all' || t.sourceId === tab).sort(byTimecode);
  const media = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);
  const pick = (t: OverviewThread) => {
    setFocus(focus === t.id ? null : t.id);
    if (t.timeUs !== undefined && media.current) {
      media.current.pause();
      media.current.currentTime = t.timeUs / 1e6;
    }
  };
  // Side by side only with room for both: the page also has the app sidebar, so
  // below ~1280px the player would shrink to ~400px next to the 380px column.
  const wide = useMediaQuery(page ? '(min-width: 1280px)' : '(min-width: 860px)');
  const labelWidth = wide ? 'w-[120px]' : 'w-[80px]';
  const threads = useMemo(() => new Map(o.threads.map((t) => [t.id, t])), [o]);
  const timed = asset.kind !== 'image';

  return (
    <div className={cx('flex gap-4', wide ? 'flex-row' : 'flex-col')} data-asset-overview={asset.id}>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <AssetPreview asset={asset} mediaRef={media} tall={page} />
        {timed ? (
          <section aria-labelledby="asset-lanes-h" className="flex flex-col gap-2.5 rounded-xl border border-line bg-panel px-3.5 py-3">
            <div className="flex items-baseline justify-between gap-2">
              <h3 id="asset-lanes-h" className="text-[13px] font-semibold">
                Comments on the scrubber, one row per link
              </h3>
              <span className="font-mono text-[11px] text-muted">{round ? `Round ${round}` : 'All rounds'}</span>
            </div>
            {linkSources.length ? (
              <>
                <TimeRuler durationUs={o.durationUs} labelWidth={labelWidth} count={5} />
                {linkSources.map((s) => (
                  <Lane
                    key={s.id}
                    sourceId={s.id}
                    label={s.name}
                    colorIndex={s.colorIndex}
                    marks={laneMarks(
                      inRound.filter((t) => t.sourceId === s.id),
                      o.durationUs,
                    )}
                    dim={tab !== 'all' && tab !== s.id}
                    focused={focus}
                    onPick={(id) => pick(threads.get(id)!)}
                    titleOf={(id) => {
                      const t = threads.get(id)!;
                      return `${t.timeUs !== undefined ? formatTimecode(t.timeUs, fps) : ''} · ${t.authorName}: ${t.body.slice(0, 140)}`;
                    }}
                    labelWidth={labelWidth}
                    height={22}
                  />
                ))}
              </>
            ) : (
              <p className="text-xs text-muted">Create a link to collect comments.</p>
            )}
          </section>
        ) : null}
        <section aria-labelledby="asset-rounds-h" className="flex flex-col gap-2.5 rounded-xl border border-line bg-panel px-3.5 py-3">
          <div className="flex items-baseline justify-between gap-2">
            <h3 id="asset-rounds-h" className="text-[13px] font-semibold">
              Rounds
            </h3>
            {round !== null && o.rounds.length ? (
              <button className="text-xs text-accent-2 hover:underline" onClick={() => setRound(null)} data-asset-rounds-all>
                Show all rounds
              </button>
            ) : null}
          </div>
          {o.rounds.length ? (
            <ol className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,260px),1fr))] gap-2.5">
              {[...o.rounds].reverse().map((r) => (
                <li key={r.round} className="min-w-0">
                  <RoundCard r={r} on={round === r.round} onPick={() => setRound(round === r.round ? null : r.round)} sourceById={sourceById} />
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-xs text-muted">Not in review yet. Turn on approvals for a link, or approve or request changes above, to start round 1.</p>
          )}
          {o.rounds.length > 1 ? (
            <p className="text-[11px] text-faint">Every round reviews the same file, so there is nothing to play side by side; compare what each round said instead.</p>
          ) : null}
        </section>
      </div>

      <aside
        aria-labelledby="asset-conv-h"
        className={cx('flex min-w-0 flex-col overflow-hidden rounded-xl border border-line bg-panel', wide ? (page ? 'w-[380px] shrink-0 self-start' : 'w-[340px] shrink-0') : 'w-full')}
        data-asset-conversations
      >
        <div className="flex flex-col gap-2.5 border-b border-line px-3.5 py-3">
          <h3 id="asset-conv-h" className="text-[13px] font-semibold">
            Conversations
          </h3>
          <div role="tablist" aria-label="Review link" className="flex flex-wrap gap-1.5">
            {[{ id: 'all', name: 'All', colorIndex: -1 } as Pick<OverviewSource, 'id' | 'name' | 'colorIndex'>, ...linkSources].map((s) => (
              <button
                key={s.id}
                role="tab"
                aria-selected={tab === s.id}
                onClick={() => setTab(s.id)}
                className={cx(
                  'inline-flex h-[30px] items-center gap-1.5 rounded-lg border px-2.5 text-xs',
                  tab === s.id ? 'border-accent bg-accent/15 text-fg' : 'border-line bg-panel-2 text-muted hover:text-fg',
                )}
                data-asset-conv-tab={s.id}
              >
                {s.colorIndex >= 0 ? <SourceSwatch index={s.colorIndex} /> : null}
                <span className="max-w-[9rem] truncate" title={s.name}>{s.name}</span>
                <span className="text-faint">{inRound.filter((t) => s.id === 'all' || t.sourceId === s.id).length}</span>
              </button>
            ))}
          </div>
        </div>
        {feed.length ? (
          <ol className={cx('divide-y divide-line overflow-y-auto', page ? (wide ? 'max-h-[calc(100vh-260px)] min-h-[200px]' : 'max-h-[70vh] min-h-[200px]') : 'max-h-[420px]')}>
            {feed.map((t) => (
              <ConversationItem key={t.id} t={t} source={sourceById.get(t.sourceId)} comments={comments} fps={fps} focused={focus === t.id} onPick={() => pick(t)} />
            ))}
          </ol>
        ) : (
          <div className="px-4 py-8 text-center text-xs text-faint">{round ? `No comments in round ${round}.` : 'No comments yet.'}</div>
        )}
        {tab !== 'all' && sourceById.get(tab) ? <Composer linkId={tab} name={sourceById.get(tab)!.name} /> : null}
      </aside>
    </div>
  );
}

function AssetPreview({ asset, mediaRef, tall }: { asset: Asset; mediaRef: React.MutableRefObject<HTMLVideoElement | HTMLAudioElement | null>; tall: boolean }) {
  const urls = useMediaUrls(asset.id, asset.proxyKey);
  const src = urls.proxy ?? urls.original;
  const cls = 'w-full rounded-xl border border-line bg-black';
  const maxH = tall ? 'max-h-[60vh]' : 'max-h-[320px]';
  if (asset.kind === 'image') return src ? <img src={src} alt={asset.name} className={cx(cls, maxH, 'object-contain')} /> : <div className={cx(cls, 'aspect-video')} />;
  if (asset.kind === 'audio' || !asset.hasVideo)
    return <audio ref={(el) => void (mediaRef.current = el)} src={src} controls className="w-full" aria-label={`${asset.name} playback`} />;
  return (
    <BufferedVideo
      elRef={(el) => void (mediaRef.current = el)}
      src={src}
      cacheKey={src && `asset:${asset.id}:${asset.proxyKey}:${urls.proxy ? 'proxy' : 'original'}`}
      controls
      playsInline
      poster={urls.poster}
      className={cx(cls, 'aspect-video object-contain', maxH)}
      aria-label={`${asset.name} playback`}
      data-asset-overview-video
    />
  );
}

function RoundCard({ r, on, onPick, sourceById }: { r: AssetRound; on: boolean; onPick: () => void; sourceById: Map<string, OverviewSource> }) {
  const res = RESULT[r.result];
  return (
    <button
      onClick={onPick}
      aria-pressed={on}
      className={cx('flex w-full min-w-0 flex-col gap-1.5 rounded-[10px] border px-3 py-2.5 text-left', on ? 'border-accent bg-accent/10' : 'border-line bg-panel-2 hover:border-line-2')}
      data-asset-round={r.round}
      data-result={r.result}
    >
      <span className="flex justify-between gap-2 text-[13px] font-semibold">
        <span>Round {r.round}</span>
        <span className={cx('font-medium', res.tone)}>{res.label}</span>
      </span>
      <span className="text-xs text-muted">
        {r.fromMs ? `${day(r.fromMs)} – ${r.current ? 'now' : day(r.toMs)}` : r.current ? 'Started, nothing yet' : 'No activity'} · {r.threads} comment{r.threads === 1 ? '' : 's'}
      </span>
      {r.sources.length ? (
        <span className="flex min-w-0 flex-wrap gap-1.5">
          {r.sources.map((d) => {
            const s = sourceById.get(d.sourceId);
            const status = d.status === 'changes' ? 'changes' : d.status;
            return (
              <span
                key={d.sourceId}
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-line bg-panel px-2 py-0.5 text-[11px]"
                title={[s?.name ?? 'Team', d.names.join(', ')].filter(Boolean).join(' · ')}
                data-round-source={d.sourceId}
                data-status={d.status}
              >
                <SourceSwatch index={s?.colorIndex ?? 0} />
                <span className="min-w-0 truncate">{s?.name ?? 'Team'}</span>
                <span className={cx('inline-flex shrink-0 items-center gap-0.5', status === 'approved' ? 'text-ok' : status === 'changes' ? 'text-warn' : 'text-muted')}>
                  <StatusIcon status={status} size={11} />
                  {status === 'approved' ? 'Approved' : status === 'changes' ? 'Changes' : 'Waiting'}
                </span>
              </span>
            );
          })}
        </span>
      ) : null}
    </button>
  );
}

function ConversationItem({
  t,
  source,
  comments,
  fps,
  focused,
  onPick,
}: {
  t: OverviewThread;
  source?: OverviewSource;
  comments: readonly Comment[];
  fps: number;
  focused: boolean;
  onPick: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const replies = open ? comments.filter((c) => c.parentId === t.id).sort((a, b) => Number(a.createdAt.microsSinceUnixEpoch - b.createdAt.microsSinceUnixEpoch)) : [];
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [focused]);
  const send = async () => {
    if (!body.trim() || !source) return;
    const ok = await attempt(() => reducers().addLinkComment({ id: uuidv7(), linkId: t.sourceId, parentId: t.id, body, timeUs: undefined, endUs: undefined, drawing: '' }));
    if (ok) setBody('');
  };
  return (
    <li
      ref={ref}
      className={cx('flex flex-col gap-1.5 px-3.5 py-2.5', focused && 'bg-accent/10', t.resolved && 'opacity-70')}
      data-asset-thread={t.id}
      data-round={t.round}
      data-focused={focused || undefined}
    >
      <div className="flex items-center gap-2 text-xs">
        {source ? <SourceSwatch index={source.colorIndex} /> : null}
        <span className="min-w-0 truncate text-muted" title={source?.name}>{source?.name}</span>
        {t.timeUs !== undefined ? (
          <button className="shrink-0 font-mono text-accent-2 hover:underline" onClick={onPick} title="Show on the scrubber">
            {formatTimecode(t.timeUs, fps)}
          </button>
        ) : null}
        <span className="ml-auto shrink-0 text-faint">{t.round ? `Round ${t.round}` : ''}</span>
      </div>
      <div className="text-[13px] leading-snug">
        <b className="font-semibold">{t.authorName}</b> <CommentBody body={t.body} className="whitespace-pre-wrap break-words" />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted">
        <button className="inline-flex items-center gap-1 hover:text-fg" onClick={() => setOpen(!open)} aria-expanded={open}>
          <MessageSquare size={11} aria-hidden="true" />
          {t.replies ? `${t.replies} ${t.replies === 1 ? 'reply' : 'replies'}` : 'Reply'}
        </button>
        <button
          className={cx('inline-flex items-center gap-1', t.resolved ? 'text-ok' : 'hover:text-ok')}
          onClick={() => void attempt(() => reducers().resolveComment({ id: t.id, resolved: !t.resolved }))}
          data-asset-thread-resolve={t.id}
        >
          {t.resolved ? <CheckCircle2 size={11} aria-hidden="true" /> : <Circle size={11} aria-hidden="true" />}
          {t.resolved ? 'Resolved · reopen' : 'Resolve'}
        </button>
        <span className="text-faint">{timeAgo(new Date(t.createdAtMs))}</span>
      </div>
      {open ? (
        <div className="space-y-1.5 border-l border-line pl-3">
          {replies.map((r) => (
            <div key={r.id} className="text-[13px]">
              <span className="font-medium">{r.authorName}</span> <span className="text-[11px] text-faint">{timeAgo(r.createdAt)}</span>
              <CommentBody body={r.body} className="block whitespace-pre-wrap break-words text-muted" />
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Input
              className="h-8 text-xs"
              placeholder="Reply (the link's reviewers see it)"
              value={body}
              maxLength={5000}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') void send();
              }}
              aria-label="Reply"
            />
            <Button size="sm" variant="primary" disabled={!body.trim()} onClick={() => void send()}>
              Send
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function Composer({ linkId, name }: { linkId: string; name: string }) {
  const [body, setBody] = useState('');
  const send = async () => {
    if (!body.trim()) return;
    const ok = await attempt(() => reducers().addLinkComment({ id: uuidv7(), linkId, parentId: undefined, body, timeUs: undefined, endUs: undefined, drawing: '' }));
    if (ok) setBody('');
  };
  return (
    <div className="flex items-center gap-2 border-t border-line bg-panel-2 px-3.5 py-2.5">
      <Input
        className="h-8 text-xs"
        placeholder={`Write to “${name}”…`}
        value={body}
        maxLength={5000}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') void send();
        }}
        aria-label={`Write to ${name}`}
        data-asset-conv-input
      />
      <Button size="sm" variant="primary" disabled={!body.trim()} onClick={() => void send()} data-asset-conv-send>
        Send
      </Button>
    </div>
  );
}
